import { useCallback, useSyncExternalStore } from "react";
import { api } from "../lib/api";

export type DemoUser = {
  id: number;
  email: string;
  name: string | null;
  stripeCustomerId: string | null;
};

const STORAGE_KEY = "stripe-payments-demo-user";

function readStored(): DemoUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DemoUser;
  } catch {
    return null;
  }
}

// Store a nível de módulo: todas as instâncias do useUser() compartilham o
// mesmo state. Sem isso, Login.setUser não notifica Nav.useUser na mesma janela
// (StorageEvent só dispara em outras tabs).
let cachedUser: DemoUser | null = readStored();
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((cb) => cb());
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

function writeUser(u: DemoUser | null): void {
  cachedUser = u;
  if (u) localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
  else localStorage.removeItem(STORAGE_KEY);
  notify();
}

// Sincroniza com mudanças vindas de outras tabs (ex: logout em outra janela).
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      cachedUser = readStored();
      notify();
    }
  });
}

// Hidrata a partir da sessão por cookie (httpOnly) na carga da página. O
// cookie é a fonte de verdade: se a sessão expirou/saiu, limpamos o cache
// local; se está válida, atualizamos o cache com os dados do servidor.
let hydrated = false;
async function hydrateFromSession(): Promise<void> {
  if (hydrated || typeof window === "undefined") return;
  hydrated = true;
  try {
    const res = await api<DemoUser & { role?: string }>("/api/auth/me");
    if (res.ok && res.body) {
      writeUser({
        id: res.body.id,
        email: res.body.email,
        name: res.body.name,
        stripeCustomerId: res.body.stripeCustomerId,
      });
    } else if (res.status === 401) {
      writeUser(null);
    }
  } catch {
    // Offline/erro de rede: mantém o cache local como está.
  }
}
void hydrateFromSession();

export function useUser() {
  const user = useSyncExternalStore(
    subscribe,
    () => cachedUser,
    () => cachedUser
  );

  const setUser = useCallback((u: DemoUser | null) => {
    writeUser(u);
  }, []);

  const updateUser = useCallback((patch: Partial<DemoUser>) => {
    if (!cachedUser) return;
    writeUser({ ...cachedUser, ...patch });
  }, []);

  const logout = useCallback(() => {
    // Encerra a sessão no servidor (limpa o cookie httpOnly) e o cache local.
    void api("/api/auth/logout", { method: "POST" });
    writeUser(null);
  }, []);

  return { user, setUser, updateUser, logout };
}
